/**
 * As Configurações como dados (docs/spec/settings.md): grupos, seções, cards e linhas. A tela desenha
 * a partir daqui, a busca procura aqui e "Restaurar padrões" sabe daqui quais preferências cada seção
 * tem. Sem React: um teste confere que toda preferência tem a sua linha.
 *
 * Os textos são chaves de i18n (`label`, `desc`, `keywords`); `keywords` são palavras a mais para a
 * busca, separadas por espaço, na língua da tela.
 */
import { DEFAULT_SETTINGS, type AppSettings } from '../settings.js';
import type { IconName } from '../ui/icons.js';

export type SectionId = 'profile' | 'account' | 'plan' | 'general' | 'chat' | 'dice' | 'notifications' | 'av' | 'support';
export type GroupId = 'account' | 'preferences' | 'support';
/** onde a seção grava: na conta (servidor) ou neste computador (localStorage) */
export type Scope = 'account' | 'device';

type Key = keyof AppSettings;
type KeyOf<V> = { [K in Key]: AppSettings[K] extends V ? K : never }[Key];
export type BoolKey = KeyOf<boolean>;
export type NumKey = KeyOf<number>;
/** os sons de aviso que dá para ouvir (notify.ts) */
export type SoundName = 'message' | 'mention' | 'roll' | 'kick' | 'pvt';
/** o que um botão de linha faz (a tela liga cada um à sua função) */
export type ActionId = 'changeEmail' | 'changePassword' | 'deleteAccount' | 'viewTerms' | 'viewPrivacy' | 'downloadLog' | 'goNoLighting' | 'resetAll' | 'forgetLayouts';
/** partes da tela que não são linhas comuns */
export type BlockId = 'profile' | 'plan' | 'chatPreview' | 'noticesBanner' | 'no3dBanner' | 'diceLook' | 'diceEffects' | 'avTest';

/** Uma opção de segmentado ou lista: o valor como texto (`'true'`, `'14'`, `'scene'`) e o rótulo. */
export type Option = { value: string; label: string };

type RowBase = { id: string; label: string; desc?: string; keywords?: string };
export type Row = RowBase &
  (
    | { kind: 'switch'; setting: BoolKey; invert?: boolean }
    | { kind: 'segmented'; setting: Key; options: readonly Option[] }
    | { kind: 'select'; setting: Key; options: readonly Option[]; descByValue?: Readonly<Record<string, string>> }
    | { kind: 'slider'; setting: NumKey; min: number; max: number; step: number; unit: 'px' | 'percent' }
    | { kind: 'sound'; setting: BoolKey; sound?: SoundName }
    | { kind: 'language' }
    | { kind: 'device'; setting: 'micDeviceId' | 'cameraDeviceId' | 'speakerDeviceId'; device: MediaDeviceKind }
    | { kind: 'info'; info: 'accountName' | 'memberSince' }
    /** uma escolha da conta, gravada no servidor na hora (não é preferência deste computador) */
    | { kind: 'privacy'; privacy: 'imsAnyone' }
    | { kind: 'action'; action: ActionId }
    | { kind: 'block'; block: BlockId; settings?: readonly Key[] }
  );

export type Card = { id: string; title: string; desc?: string; tone?: 'danger' | 'warn'; rows: readonly Row[] };

/** O que a busca acha dentro de um bloco (o perfil, o plano): `id` é o da linha na tela. */
export type Extra = { id: string; label: string; keywords?: string };

export type Section = {
  id: SectionId;
  group: GroupId;
  title: string;
  sub: string;
  icon: IconName;
  scope: Scope;
  /** tem "Restaurar padrões" no fim */
  resettable: boolean;
  /** blocos antes dos cards (a prévia do chat, o aviso das notificações, o dos dados sem 3D, o perfil inteiro) */
  top?: readonly BlockId[];
  cards: readonly Card[];
  extras?: readonly Extra[];
  keywords?: string;
};

const sw = (id: BoolKey, label: string, desc?: string, extra: Partial<RowBase> = {}): Row => ({ kind: 'switch', id, setting: id, label, ...(desc ? { desc } : {}), ...extra });
const options = (...pairs: [string, string][]): Option[] => pairs.map(([value, label]) => ({ value, label }));

export const GROUPS: readonly { id: GroupId; label: string }[] = [
  { id: 'account', label: 'prefs.group.account' },
  { id: 'preferences', label: 'prefs.group.preferences' },
  { id: 'support', label: 'prefs.group.support' },
];

export const SECTIONS: readonly Section[] = [
  {
    id: 'profile',
    group: 'account',
    title: 'prefs.profile.title',
    sub: 'prefs.profile.sub',
    icon: 'user',
    scope: 'account',
    resettable: false,
    top: ['profile'],
    cards: [],
    keywords: 'prefs.profile.kw',
    extras: [
      { id: 'avatar', label: 'config.profile.avatar', keywords: 'prefs.profile.avatar.kw' },
      { id: 'cover', label: 'config.profile.cover', keywords: 'prefs.profile.cover.kw' },
      { id: 'bio', label: 'profile.bio' },
      { id: 'rpg', label: 'profile.rpg' },
      { id: 'times', label: 'profile.times' },
    ],
  },
  {
    id: 'account',
    group: 'account',
    title: 'prefs.account.title',
    sub: 'prefs.account.sub',
    icon: 'lock',
    scope: 'account',
    resettable: false,
    keywords: 'prefs.account.kw',
    cards: [
      {
        id: 'access',
        title: 'prefs.account.access',
        rows: [
          { kind: 'info', id: 'accountName', info: 'accountName', label: 'config.account.name' },
          // o e-mail de agora e a troca, que confirma o endereço novo antes de valer (docs/spec/email.md)
          { kind: 'action', id: 'email', action: 'changeEmail', label: 'prefs.account.email', desc: 'prefs.account.email.desc', keywords: 'prefs.account.email.kw' },
          { kind: 'info', id: 'memberSince', info: 'memberSince', label: 'prefs.account.since' },
          { kind: 'action', id: 'password', action: 'changePassword', label: 'prefs.account.password', desc: 'prefs.account.password.desc', keywords: 'prefs.account.password.kw' },
        ],
      },
      {
        id: 'privacy',
        title: 'prefs.account.privacy',
        rows: [
          { kind: 'privacy', id: 'imsAnyone', privacy: 'imsAnyone', label: 'prefs.account.imsAnyone', desc: 'prefs.account.imsAnyone.desc', keywords: 'prefs.account.imsAnyone.kw' },
          // os textos que a conta aceitou (docs/spec/termos.md)
          { kind: 'action', id: 'terms', action: 'viewTerms', label: 'legal.terms.title', desc: 'prefs.account.terms.desc', keywords: 'prefs.account.terms.kw' },
          { kind: 'action', id: 'privacyPolicy', action: 'viewPrivacy', label: 'legal.privacy.title', desc: 'prefs.account.privacyPolicy.desc', keywords: 'prefs.account.privacyPolicy.kw' },
        ],
      },
      {
        id: 'danger',
        title: 'prefs.account.danger',
        tone: 'danger',
        rows: [{ kind: 'action', id: 'deleteAccount', action: 'deleteAccount', label: 'prefs.account.delete', desc: 'prefs.account.delete.desc', keywords: 'prefs.account.delete.kw' }],
      },
    ],
  },
  {
    id: 'plan',
    group: 'account',
    title: 'prefs.plan.title',
    sub: 'prefs.plan.sub',
    icon: 'cloud',
    scope: 'account',
    resettable: false,
    top: ['plan'],
    cards: [],
    keywords: 'prefs.plan.kw',
    extras: [
      { id: 'storage', label: 'plans.entitlement.storageBytes' },
      { id: 'maxFile', label: 'plans.entitlement.maxFileBytes' },
      { id: 'compare', label: 'config.plan.compare' },
    ],
  },
  {
    id: 'general',
    group: 'preferences',
    title: 'prefs.general.title',
    sub: 'prefs.general.sub',
    icon: 'sliders',
    scope: 'device',
    resettable: true,
    cards: [
      {
        id: 'app',
        title: 'config.general.app',
        rows: [
          { kind: 'language', id: 'language', label: 'common.language', keywords: 'prefs.general.language.kw' },
          { kind: 'segmented', id: 'startPage', setting: 'startPage', label: 'config.general.startPage', options: options(['inicio', 'logged.nav.dashboard'], ['salas', 'logged.nav.rooms']) },
        ],
      },
      {
        id: 'explore',
        title: 'prefs.general.explore',
        rows: [
          {
            kind: 'segmented',
            id: 'hideAdultRooms',
            setting: 'hideAdultRooms',
            label: 'prefs.general.adult',
            desc: 'prefs.general.adult.desc',
            keywords: 'prefs.general.adult.kw',
            options: options(['true', 'prefs.general.adult.hide'], ['false', 'prefs.general.adult.show']),
          },
        ],
      },
      {
        id: 'table',
        title: 'prefs.general.table',
        rows: [
          {
            kind: 'select',
            id: 'distanceUnit',
            setting: 'distanceUnit',
            label: 'prefs.general.distance',
            keywords: 'prefs.general.distance.kw',
            options: options(['scene', 'prefs.general.distance.scene'], ['m', 'prefs.general.distance.m'], ['ft', 'prefs.general.distance.ft'], ['both', 'prefs.general.distance.both']),
            descByValue: { scene: 'prefs.general.distance.desc.scene', m: 'prefs.general.distance.desc.m', ft: 'prefs.general.distance.desc.ft', both: 'prefs.general.distance.desc.both' },
          },
        ],
      },
    ],
  },
  {
    id: 'chat',
    group: 'preferences',
    title: 'prefs.chat.title',
    sub: 'prefs.chat.sub',
    icon: 'chat',
    scope: 'device',
    resettable: true,
    top: ['chatPreview'],
    cards: [
      {
        id: 'reading',
        title: 'prefs.chat.reading',
        rows: [
          { kind: 'slider', id: 'chatFontSize', setting: 'chatFontSize', label: 'config.general.chatSize', keywords: 'prefs.chat.size.kw', min: 12, max: 20, step: 1, unit: 'px' },
          { kind: 'segmented', id: 'chatSpacing', setting: 'chatSpacing', label: 'config.general.chatSpacing', options: options(['normal', 'config.general.spacing.normal'], ['compact', 'config.general.spacing.compact']) },
          sw('showTimestamps', 'config.general.timestamps', undefined, { keywords: 'prefs.chat.timestamps.kw' }),
        ],
      },
      {
        id: 'formatting',
        title: 'prefs.chat.formatting',
        desc: 'config.text.hint',
        rows: [
          sw('tmHeadings', 'config.text.headings'),
          sw('tmHorizontalLines', 'config.text.lines'),
          sw('tmSmileys', 'config.text.smileys', undefined, { keywords: 'prefs.chat.smileys.kw' }),
          sw('tmInitialCaps', 'config.text.initialCaps'),
        ],
      },
      { id: 'table', title: 'prefs.chat.table', rows: [sw('barNotices', 'prefs.chat.bars', 'prefs.chat.bars.desc', { keywords: 'prefs.chat.bars.kw' })] },
      { id: 'friends', title: 'prefs.chat.friends', rows: [sw('showFriendsChat', 'config.general.friends.show', 'config.general.friends.hint')] },
    ],
  },
  {
    id: 'dice',
    group: 'preferences',
    title: 'prefs.dice.title',
    sub: 'prefs.dice.sub',
    icon: 'die',
    scope: 'device',
    resettable: true,
    keywords: 'prefs.dice.kw',
    top: ['no3dBanner'],
    cards: [
      { id: 'look', title: 'prefs.dice.look', rows: [{ kind: 'block', id: 'diceLook', block: 'diceLook', label: 'prefs.dice.look', keywords: 'prefs.dice.look.kw', settings: ['diceCustom', 'diceBody', 'diceNumber', 'diceTexture', 'diceTextureScale'] }] },
      {
        id: 'display',
        title: 'prefs.dice.display',
        rows: [
          sw('show3DDice', 'config.general.dice3d'),
          sw('showDiceEffects', 'config.general.diceEffects'),
          { kind: 'switch', id: 'dice3DLighting', setting: 'dice3DLighting', invert: true, label: 'prefs.dice.noLighting', desc: 'prefs.dice.noLighting.desc', keywords: 'prefs.dice.noLighting.kw' },
        ],
      },
      {
        id: 'effects',
        title: 'config.dice.effects',
        desc: 'config.dice.effects.hint',
        rows: [{ kind: 'block', id: 'diceEffects', block: 'diceEffects', label: 'config.dice.effects', keywords: 'prefs.dice.effects.kw', settings: ['diceEffects'] }],
      },
    ],
  },
  {
    id: 'notifications',
    group: 'preferences',
    title: 'prefs.notify.title',
    sub: 'prefs.notify.sub',
    icon: 'bell',
    scope: 'device',
    resettable: true,
    top: ['noticesBanner'],
    cards: [
      {
        id: 'sounds',
        title: 'config.notify.sounds',
        rows: [
          { kind: 'slider', id: 'masterVolume', setting: 'masterVolume', label: 'prefs.notify.volume', keywords: 'prefs.notify.volume.kw', min: 0, max: 1, step: 0.05, unit: 'percent' },
          { kind: 'sound', id: 'soundMessages', setting: 'soundMessages', keywords: 'prefs.notify.sound.kw', sound: 'message', label: 'config.notify.soundMessages' },
          { kind: 'sound', id: 'soundMentions', setting: 'soundMentions', keywords: 'prefs.notify.sound.kw', sound: 'mention', label: 'config.notify.soundMentions' },
          { kind: 'sound', id: 'soundPvt', setting: 'soundPvt', keywords: 'prefs.notify.sound.kw', sound: 'pvt', label: 'config.notify.soundPvt' },
          { kind: 'sound', id: 'soundKick', setting: 'soundKick', keywords: 'prefs.notify.sound.kw', sound: 'kick', label: 'config.notify.soundKick' },
          { kind: 'sound', id: 'soundRolls', setting: 'soundRolls', keywords: 'prefs.notify.sound.kw', sound: 'roll', label: 'config.notify.soundRolls' },
          { kind: 'sound', id: 'soundScene', setting: 'soundScene', label: 'prefs.notify.soundScene', desc: 'prefs.notify.soundScene.desc', keywords: 'prefs.notify.sound.kw' },
        ],
      },
      {
        id: 'notices',
        title: 'config.notify.visual',
        rows: [
          {
            kind: 'segmented',
            id: 'messageNotice',
            setting: 'messageNotice',
            label: 'prefs.notify.messages',
            options: options(['always', 'config.notify.always'], ['unfocused', 'prefs.notify.unfocused'], ['never', 'config.notify.never']),
          },
          sw('flashTitle', 'prefs.notify.flash', 'prefs.notify.flash.desc'),
        ],
      },
    ],
  },
  {
    id: 'av',
    group: 'preferences',
    title: 'prefs.av.title',
    sub: 'prefs.av.sub',
    icon: 'mic',
    scope: 'device',
    resettable: true,
    keywords: 'prefs.av.kw',
    cards: [
      { id: 'test', title: 'prefs.av.test', desc: 'prefs.av.test.desc', rows: [{ kind: 'block', id: 'avTest', block: 'avTest', label: 'prefs.av.test', keywords: 'prefs.av.test.kw' }] },
      {
        id: 'devices',
        title: 'prefs.av.devices',
        rows: [
          { kind: 'device', id: 'micDeviceId', setting: 'micDeviceId', device: 'audioinput', label: 'config.conf.mic' },
          { kind: 'device', id: 'cameraDeviceId', setting: 'cameraDeviceId', device: 'videoinput', label: 'config.conf.camera' },
          { kind: 'device', id: 'speakerDeviceId', setting: 'speakerDeviceId', device: 'audiooutput', label: 'prefs.av.speaker', desc: 'prefs.av.speaker.desc', keywords: 'prefs.av.speaker.kw' },
        ],
      },
      { id: 'join', title: 'prefs.av.join', rows: [sw('joinWithMicOn', 'config.conf.micOn'), sw('joinWithCameraOn', 'config.conf.camOn')] },
    ],
  },
  {
    id: 'support',
    group: 'support',
    title: 'prefs.support.title',
    sub: 'prefs.support.sub',
    icon: 'help',
    scope: 'device',
    resettable: false,
    keywords: 'prefs.support.kw',
    cards: [
      {
        id: 'diagnostics',
        title: 'prefs.support.diagnostics',
        rows: [
          { kind: 'action', id: 'errorLog', action: 'downloadLog', label: 'prefs.support.log', desc: 'config.utils.debugHint', keywords: 'prefs.support.log.kw' },
          { kind: 'action', id: 'slowDice', action: 'goNoLighting', label: 'prefs.support.dice', desc: 'prefs.support.dice.desc' },
        ],
      },
      {
        id: 'restart',
        title: 'config.advanced.reset',
        desc: 'config.advanced.hint',
        tone: 'warn',
        rows: [
          { kind: 'action', id: 'resetAll', action: 'resetAll', label: 'prefs.support.resetAll', desc: 'prefs.support.resetAll.desc', keywords: 'prefs.support.resetAll.kw' },
          { kind: 'action', id: 'forgetLayouts', action: 'forgetLayouts', label: 'prefs.support.layouts', desc: 'config.advanced.resetLayoutsConfirm', keywords: 'prefs.support.layouts.kw' },
        ],
      },
    ],
  },
];

/** Preferências que se mudam dentro da mesa (o volume da música e dos efeitos, o mudo), não aqui. */
export const ROOM_ONLY_SETTINGS: readonly Key[] = ['musicVolume', 'sfxVolume', 'audioMuted'];

export function sectionById(id: SectionId): Section {
  return SECTIONS.find((section) => section.id === id)!;
}

/** As preferências de uma linha (vazio: a linha não grava nada deste computador). */
export function rowSettings(row: Row): readonly Key[] {
  switch (row.kind) {
    case 'switch':
    case 'segmented':
    case 'select':
    case 'slider':
    case 'sound':
    case 'device':
      return [row.setting];
    case 'block':
      return row.settings ?? [];
    default:
      return [];
  }
}

/** As preferências da seção, para "Restaurar padrões" (o idioma não é preferência daqui). */
export function sectionSettingKeys(id: SectionId): Key[] {
  return sectionById(id).cards.flatMap((card) => card.rows.flatMap(rowSettings));
}

/** Os valores de fábrica das preferências da seção. */
export function sectionDefaults(id: SectionId): Partial<AppSettings> {
  return Object.fromEntries(sectionSettingKeys(id).map((key) => [key, DEFAULT_SETTINGS[key]])) as Partial<AppSettings>;
}

/** Nomes das páginas antigas (links salvos, `go({ kind: 'settings', page })` de fora). */
const PAGE_ALIASES: Readonly<Record<string, SectionId>> = { text: 'chat', conference: 'av', utilities: 'support', advanced: 'support' };

/** A seção de um nome de página, ou null para um nome desconhecido. */
export function resolveSection(page: string | undefined): SectionId | null {
  if (!page) return null;
  if (SECTIONS.some((section) => section.id === page)) return page as SectionId;
  return PAGE_ALIASES[page] ?? null;
}

/** O valor da preferência como texto de opção (`'true'`, `'14'`, `'scene'`). */
export function optionValue(settings: AppSettings, key: Key): string {
  return String(settings[key]);
}

/** O texto de uma opção de volta ao tipo da preferência. */
export function parseOption(key: Key, raw: string): AppSettings[Key] {
  const sample = DEFAULT_SETTINGS[key];
  if (typeof sample === 'boolean') return raw === 'true';
  if (typeof sample === 'number') return Number(raw);
  return raw as AppSettings[Key];
}

/* ------------------------------------------------------------------ busca */

export type SearchResult = { section: SectionId; row: string | null; label: string; path: string };
type Translate = (key: string, params?: Record<string, string | number>) => string;

/** Sem acentos e em minúsculas: "Notificações" acha "notificacoes". */
export function fold(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** As palavras de um texto, sem acento e em minúsculas. */
function words(...texts: (string | undefined)[]): string[] {
  return fold(texts.filter(Boolean).join(' '))
    .split(/[^\p{L}\p{N}@+#-]+/u)
    .filter(Boolean);
}

/**
 * Quanto o texto casa com a busca: toda palavra buscada precisa começar alguma palavra do texto
 * ("notif" acha "Notificações"); a palavra inteira vale mais, e casar no rótulo vale mais ainda. 0 é
 * não casar.
 */
function score(query: readonly string[], label: string, ...rest: (string | undefined)[]): number {
  const inLabel = words(label);
  const all = [...inLabel, ...words(...rest)];
  let total = 0;
  for (const q of query) {
    const whole = all.includes(q);
    if (!whole && !all.some((w) => w.startsWith(q))) return 0;
    total += (whole ? 2 : 1) + (inLabel.some((w) => w.startsWith(q)) ? 1 : 0);
  }
  return total;
}

/**
 * Procura nas linhas (rótulo, descrição e palavras-chave), nos blocos e nas seções. A seção só vira
 * resultado quando nenhuma linha dela casou, para não repetir. Os que casam melhor vêm primeiro (a
 * palavra inteira antes do pedaço: "som" mostra os sons antes de "sombra"); no máximo `limit`.
 */
export function searchSettings(query: string, t: Translate, limit = 12): SearchResult[] {
  const q = words(query);
  if (!q.length) return [];
  const results: (SearchResult & { score: number })[] = [];
  for (const section of SECTIONS) {
    const title = t(section.title);
    const hits: (SearchResult & { score: number })[] = [];
    for (const card of section.cards) {
      for (const row of card.rows) {
        const value = score(q, t(row.label), row.desc ? t(row.desc) : undefined, row.keywords ? t(row.keywords) : undefined);
        if (value) hits.push({ section: section.id, row: row.id, label: t(row.label), path: `${title} › ${t(card.title)}`, score: value });
      }
    }
    for (const extra of section.extras ?? []) {
      const value = score(q, t(extra.label), extra.keywords ? t(extra.keywords) : undefined);
      if (value) hits.push({ section: section.id, row: extra.id, label: t(extra.label), path: title, score: value });
    }
    if (!hits.length) {
      const value = score(q, title, t(section.sub), section.keywords ? t(section.keywords) : undefined);
      if (value) hits.push({ section: section.id, row: null, label: title, path: t('prefs.search.section'), score: value });
    }
    results.push(...hits);
  }
  // a ordenação é estável: no empate, fica a ordem da tela
  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ score: _score, ...result }) => result);
}

/** Todas as chaves de texto do registro (o teste confere que existem nas duas línguas). */
export function registryTextKeys(): string[] {
  const keys = new Set<string>(GROUPS.map((group) => group.label));
  for (const section of SECTIONS) {
    for (const key of [section.title, section.sub, section.keywords]) if (key) keys.add(key);
    for (const extra of section.extras ?? []) for (const key of [extra.label, extra.keywords]) if (key) keys.add(key);
    for (const card of section.cards) {
      for (const key of [card.title, card.desc]) if (key) keys.add(key);
      for (const row of card.rows) {
        for (const key of [row.label, row.desc, row.keywords]) if (key) keys.add(key);
        if (row.kind === 'segmented' || row.kind === 'select') for (const option of row.options) keys.add(option.label);
        if (row.kind === 'select') for (const key of Object.values(row.descByValue ?? {})) keys.add(key);
      }
    }
  }
  return [...keys];
}

/* ------------------------------------------------------------------ resumo (a lista do celular) */

/** O resumo ao lado do nome da seção na lista do celular ("Português", "14 px"); vazio se não há. */
export function sectionSummary(id: SectionId, settings: AppSettings, t: Translate, languageLabel: string): string {
  switch (id) {
    case 'general':
      return languageLabel;
    case 'chat':
      return `${settings.chatFontSize} px`;
    case 'dice':
      return t(settings.diceCustom ? 'prefs.summary.myColors' : 'prefs.summary.default');
    case 'notifications':
      return `${Math.round(settings.masterVolume * 100)}%`;
    default:
      return '';
  }
}
